import type { ContactType } from '~/types/person/contacts';
import type { License } from '~/types/person/person';

export enum LicenseType {
    ESSENTIALS = 'essentials',
    PRO = 'pro',
    SIGNATURE = 'signature',
}

export type StorageAction = "set" | "get" | "remove";

export const LicenseTypeName: Record<LicenseType, string> = {
    [LicenseType.ESSENTIALS]: 'Essentials',
    [LicenseType.PRO]: 'Pro',
    [LicenseType.SIGNATURE]: 'Signature',
  };


export enum LicenseRenewalTerm {
    MONTHLY = 1,
    YEARLY = 12,
    TWO_YEARS = 24,
    THREE_YEARS = 36,
}

export const LicenseRenewalTermKey: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'monthly',
    [LicenseRenewalTerm.YEARLY]: 'yearly',
    [LicenseRenewalTerm.TWO_YEARS]: 'two_year',
    [LicenseRenewalTerm.THREE_YEARS]: 'three_year',
  };


// ✅ Instead of a second enum, use a Record typed by LicenseRenewalTerm
export const LicenseRenewalTermDisplay: Record<LicenseRenewalTerm, string> = {
    [LicenseRenewalTerm.MONTHLY]: 'Monthly',
    [LicenseRenewalTerm.YEARLY]: 'Yearly',
    [LicenseRenewalTerm.TWO_YEARS]: '2 Years',
    [LicenseRenewalTerm.THREE_YEARS]: '3 Years',
  };


export enum SubscriptionType {
    INDIVIDUAL = 1,
    COUPLES = 2,
    FAMILY = 4,
}

export interface PurchaseLicenseModalData {
    type: PurchaseType;
    license?: License;
    for?: number;
    single?: boolean;
    contact?: PurchaseLicenseFor; // Contact object for gift purchases
}

export interface PurchaseLicenseFor {
    type: ContactType;
    id: number;
    name: string;
}

export interface AssignLicenseModalData {
    license: License;
}

export type PricingData = {
    original: number;
    yearlyDiscount: number;
    unitPrice?: number;
};

export const PricingTable: Record<LicenseType, Record<LicenseRenewalTerm, PricingData>> = {
    [LicenseType.ESSENTIALS]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 19.0, yearlyDiscount: 0.0, unitPrice: 19.0 },
        [LicenseRenewalTerm.YEARLY]: { original: 228.0, yearlyDiscount: 132.03, unitPrice: 95.97 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 456.0, yearlyDiscount: 287.05, unitPrice: 168.95 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 684.0, yearlyDiscount: 469.5, unitPrice: 214.5 },
    },
    [LicenseType.PRO]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 39.0, yearlyDiscount: 0.0, unitPrice: 39.0 },
        [LicenseRenewalTerm.YEARLY]: { original: 468.0, yearlyDiscount: 271.0, unitPrice: 197.0 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 936.0, yearlyDiscount: 589.21, unitPrice: 346.79 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 1404.0, yearlyDiscount: 963.71, unitPrice: 440.29 },
    },
    [LicenseType.SIGNATURE]: {
        [LicenseRenewalTerm.MONTHLY]: { original: 0, yearlyDiscount: 0, unitPrice: 0 }, // No monthly plan for Signature
        [LicenseRenewalTerm.YEARLY]: { original: 1899.0, yearlyDiscount: 1099.63, unitPrice: 799.37 },
        [LicenseRenewalTerm.TWO_YEARS]: { original: 3798.0, yearlyDiscount: 2390.84, unitPrice: 1407.16 },
        [LicenseRenewalTerm.THREE_YEARS]: { original: 5697.0, yearlyDiscount: 3910.42, unitPrice: 1786.58 },
    },
};


// Multi-license discount structure matching API response
export const MULTI_LICENSE_DISCOUNTS: Record<number, number> = {
    1: 0, // No discount for 1st license
    2: 0.1, // 10% discount for 2nd license
    3: 0.12, // 12% discount for 3rd license
    4: 0.15, // 15% discount for 4th+ licenses
} as const;

// License benefits by type
export const LICENSE_BENEFITS: Record<LicenseType, string[]> = {
    [LicenseType.ESSENTIALS]: [
        '919+ Data Brokers & People Search Sites covered',
        'Mostly Automated removals, with human support',
        'Unlimited emails, phones, addresses, and alt. names',
        'Flexible Priority lower cost for non-urgent removals',
        'Continuous Monitoring with full monthly re-scans',
        'Real-Time Dashboard reporting on current efforts',
        'Monthly Email Report summarizing your progress',
    ],
    [LicenseType.PRO]: [
        'Everything in Essentials',
        '178,274 Custom Sites covered for data removals',
        'Google, Yahoo, and Bing search monitoring',
        'Search Result Removal submitted on all deletions',
        'Social Media Listening critical doxxing protection',
        'Paste Site Coverage monitoring and removals',
        'Dark Web Monitoring for high-risk data breaches',
        'Mass-Marketing Opt-Out from major spam lists',
        'Blur My House on Google Maps and Apple Maps',
    ],
    [LicenseType.SIGNATURE]: [
        'Everything in Pro',
        'Priority Expedited Removals for faster takedowns',
        'US-Based Analysts exclusively handling removals',
        'Complex Takedowns requiring custom effort',
        'Legal Options reserved for maximum success rates',
        '24/7/365 SOC Escalation for critical exposures',
    ],
} as const;

// Function to update the dynamic values
export const setLicenseStats = (dataBrokers: number, customSites: number) => {
    LICENSE_BENEFITS[LicenseType.ESSENTIALS][0] = `${dataBrokers}+ Data Brokers & People Search Sites covered`;
    LICENSE_BENEFITS[LicenseType.PRO][1] = `${customSites} Standard Sites covered for data removals`;
};

// Standard License Costs API Response Types
export interface MultiLicensePricing {
    count: number;
    price_per_license: number;
}

export interface TermPricing {
    term: 'monthly' | 'yearly' | 'two_year' | 'three_year';
    months: number;
    multi_licenses: MultiLicensePricing[];
}

export interface ExistingLicenses {
    monthly: number;
    yearly: number;
    two_year: number;
    three_year: number;
}

export interface LicenseStandardCosts {
    base_price_monthly: number;
    pricing: TermPricing[];
    existing_licenses: ExistingLicenses;
}

export interface StandardLicenseCostsResponse {
    [LicenseType.ESSENTIALS]: LicenseStandardCosts;
    [LicenseType.PRO]: LicenseStandardCosts;
    [LicenseType.SIGNATURE]: LicenseStandardCosts;
}

export enum PurchaseType {
    PURCHASE,
    UPGRADE,
    GIFT,
    REFUND,
}

export interface UpdateLicensePayload {  
    renew: boolean;
}

export interface AssignLicensePayload {
    assigned_to_id: number;
    type: ContactType;
}

export interface RefundLicenseResponse {
    status: string;
    amount: number;
    amount_formatted: string;
    refunded_at: string;
    message: string;
}